import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';
import { handleScheduleTourClick, scheduleTourHref } from '../utils/openScheduleTour';

const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function ThankYouContent() {
  useRevealStyles();
  useSiteCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const primaryCta = useSlotGroup('thankyoucontent.primary_cta', primaryCtaDef);
  const secondaryCta = useSlotGroup('thankyoucontent.secondary_cta', secondaryCtaDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="thankyoucontent.body"
          label="Body Text"
          defaultValue="<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>"
          as="div"
          className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
        />

        <div className="tyc-cta-row flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <EditableSlotGroup groupPath="thankyoucontent.primary_cta" definition={primaryCtaDef}>
            <a
              href={primaryCta.url || '/floor-plans'}
              target={primaryCta._target || '_self'}
              className="site-cta site-cta--primary"
            >
              {primaryCta.label || 'View Floor Plans'}
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="thankyoucontent.secondary_cta" definition={secondaryCtaDef}>
            <a
              href={scheduleTourHref(secondaryCta.url)}
              target={secondaryCta._target || '_self'}
              className="site-cta site-cta--secondary"
              data-vibesite-event="schedule_tour_clicked"
              onClickCapture={(e) =>
                handleScheduleTourClick(e, secondaryCta.url || '/schedule-a-tour')
              }
            >
              {secondaryCta.label || 'Schedule a Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Match PropertyOverview / AmenitiesForYou / AmenitiesList /
           ContactForm: filled CTA uses primary at rest, surface on hover
           with luminance-based ink so contrast tracks the brand palette. */
        .tyc-cta-row {
          --site-cta-secondary-accent: var(--vibe-color-primary);
          --site-cta-secondary-on-accent: var(--site-cta-ink-on-primary, var(--vibe-color-text-on-primary, #ffffff));
          --site-cta-hover-accent: var(--vibe-color-surface);
          --site-cta-hover-on-accent: var(--site-cta-ink-on-surface, #ffffff);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYouContent',
  displayName: 'Thank You Content',
  tokens: {
    'thankyoucontent.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue: '<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>',
    },
  },
  slotGroups: {
    'thankyoucontent.primary_cta': primaryCtaDef,
    'thankyoucontent.secondary_cta': secondaryCtaDef,
  },
};

(ThankYouContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ThankYouContent;
